/* Web-push subscription helper.
 *
 * Call requestPushPermission() at a contextual moment (e.g. after the
 * first order confirmation) — NOT on app boot. Browsers throttle the
 * permission prompt if it's shown without user gesture.
 *
 * The server endpoint /push/subscribe stores the subscription per user,
 * keyed by Authorization. To unsubscribe, call removePushSubscription().
 */

import { config } from './config.js';
import { api } from './api.js';
import { getToken } from './auth.js';

function urlBase64ToUint8Array(base64) {
  const padding = '='.repeat((4 - (base64.length % 4)) % 4);
  const raw = atob((base64 + padding).replace(/-/g, '+').replace(/_/g, '/'));
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

export async function initPush() {
  // Don't prompt; just register the SW if browser supports it.
  // The actual subscribe step is gated by requestPushPermission().
  if (!('serviceWorker' in navigator)) return;
  // vite-plugin-pwa handles SW registration in its `injectRegister: 'auto'` mode.
}

export async function requestPushPermission() {
  if (!('Notification' in window) || !('serviceWorker' in navigator) || !('PushManager' in window)) {
    return { ok: false, reason: 'unsupported' };
  }
  if (!config.PUSH_PUBLIC_KEY) {
    return { ok: false, reason: 'no_vapid_key' };
  }
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') return { ok: false, reason: 'denied' };

  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(config.PUSH_PUBLIC_KEY),
  });

  if (!getToken()) return { ok: false, reason: 'not_signed_in', subscription: sub };

  if (!config.IS_MOCK) {
    await api.post('/push/subscribe', sub.toJSON());
  }
  return { ok: true, subscription: sub };
}

export async function removePushSubscription() {
  if (!('serviceWorker' in navigator)) return;
  const reg = await navigator.serviceWorker.ready;
  const sub = await reg.pushManager.getSubscription();
  if (sub) {
    if (!config.IS_MOCK) {
      try { await api.post('/push/unsubscribe', { endpoint: sub.endpoint }); } catch { /* ignore */ }
    }
    await sub.unsubscribe();
  }
}
